import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpc } from "@/lib/trpc";
import { formatDate } from "@/lib/utils";
import { DollarSign, Plus, Pencil, Trash2, Save, AlertCircle, RefreshCw, Coins } from "lucide-react";
import { toast } from "sonner";

export default function Pricing() {
  const [showForm, setShowForm] = useState(false);
  const [editKey, setEditKey] = useState<string | null>(null);
  const [formKey, setFormKey] = useState("");
  const [formValue, setFormValue] = useState("");
  const [formDesc, setFormDesc] = useState("");

  const [creditForm, setCreditForm] = useState(false);
  const [creditSlug, setCreditSlug] = useState("");
  const [creditPrice, setCreditPrice] = useState("");

  const { data: configs, isLoading, isError, refetch } = trpc.admin.systemSettings.pricingList.useQuery();

  const { data: creditPricing, isLoading: creditLoading, refetch: refetchCredit } =
    trpc.admin.travauxbtp.getCreditPricing.useQuery();

  const updateCreditMutation = trpc.admin.travauxbtp.updateCreditPricing.useMutation({
    onSuccess: () => {
      toast.success("Prix crédit mis à jour");
      setCreditForm(false);
      setCreditSlug("");
      setCreditPrice("");
      refetchCredit();
    },
    onError: (err) => toast.error(err.message),
  });

  const upsertMutation = trpc.admin.systemSettings.pricingUpsert.useMutation({
    onSuccess: () => {
      toast.success("Configuration sauvegardée");
      setShowForm(false);
      setEditKey(null);
      setFormKey("");
      setFormValue("");
      setFormDesc("");
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteMutation = trpc.admin.systemSettings.pricingDelete.useMutation({
    onSuccess: () => {
      toast.success("Configuration supprimée");
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  function openEdit(config: any) {
    setEditKey(config.key);
    setFormKey(config.key);
    setFormValue(config.value);
    setFormDesc(config.description || "");
    setShowForm(true);
  }

  function openCreate() {
    setEditKey(null);
    setFormKey("");
    setFormValue("");
    setFormDesc("");
    setShowForm(true);
  }

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Configuration Pricing</h1>
            <p className="text-muted-foreground">Paramètres tarifaires et limites par plan</p>
          </div>
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Ajouter
          </Button>
        </div>

        {/* Pricing Parameters */}
        <Card>
          <CardHeader>
            <CardTitle>Paramètres</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : isError ? (
              <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                <AlertCircle className="h-12 w-12 text-destructive/40" />
                <p className="text-muted-foreground font-medium">Erreur de chargement de la configuration</p>
                <Button variant="outline" onClick={() => refetch()}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Réessayer
                </Button>
              </div>
            ) : configs && configs.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Clé</TableHead>
                    <TableHead>Valeur</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Mis à jour</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {configs.map((c: any) => (
                    <TableRow key={c.key}>
                      <TableCell>
                        <code className="text-sm font-mono bg-muted px-1 py-0.5 rounded">{c.key}</code>
                      </TableCell>
                      <TableCell className="font-medium">{c.value}</TableCell>
                      <TableCell className="text-muted-foreground">{c.description || "—"}</TableCell>
                      <TableCell>{formatDate(c.updatedAt)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => openEdit(c)}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => deleteMutation.mutate({ key: c.key })}
                            disabled={deleteMutation.isPending}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <DollarSign className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                <p className="text-muted-foreground">Aucune configuration pricing</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Credit Pricing TravauxBTP */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Coins className="h-5 w-5 text-warning" />
                Tarifs crédits TravauxBTP
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">Prix par catégorie de métier (en centimes)</p>
            </div>
            <Button size="sm" onClick={() => setCreditForm(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Ajouter catégorie
            </Button>
          </CardHeader>
          <CardContent>
            {creditLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : creditPricing ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                  <DollarSign className="h-5 w-5 text-success" />
                  <div>
                    <p className="text-sm font-medium">Prix par défaut</p>
                    <p className="text-2xl font-bold">{(creditPricing.defaultPrice / 100).toFixed(2)} €</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="ml-auto"
                    onClick={() => {
                      setCreditSlug("default");
                      setCreditPrice(String(creditPricing.defaultPrice));
                      setCreditForm(true);
                    }}
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                </div>
                {Object.keys(creditPricing.overrides ?? {}).length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Catégorie</TableHead>
                        <TableHead>Prix (centimes)</TableHead>
                        <TableHead>Prix (€)</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.entries(creditPricing.overrides ?? {}).map(([slug, price]) => (
                        <TableRow key={slug}>
                          <TableCell>
                            <code className="text-sm font-mono bg-muted px-1 py-0.5 rounded">{slug}</code>
                          </TableCell>
                          <TableCell className="font-medium">{price as number}</TableCell>
                          <TableCell>{((price as number) / 100).toFixed(2)} €</TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setCreditSlug(slug);
                                setCreditPrice(String(price));
                                setCreditForm(true);
                              }}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Aucune surcharge par catégorie — le prix par défaut s'applique partout.
                  </p>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <Coins className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                <p className="text-muted-foreground">Aucune configuration de crédits</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Credit pricing form dialog */}
        <Dialog open={creditForm} onOpenChange={setCreditForm}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {creditSlug ? `Modifier le prix — ${creditSlug}` : "Ajouter un prix par catégorie"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label htmlFor="credit-slug">Catégorie (slug)</Label>
                <Input
                  id="credit-slug"
                  placeholder="ex: electricite, plomberie, default"
                  value={creditSlug}
                  onChange={(e) => setCreditSlug(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="credit-price">Prix (centimes)</Label>
                <Input
                  id="credit-price"
                  type="number"
                  placeholder="ex: 1200 (= 12,00 €)"
                  value={creditPrice}
                  onChange={(e) => setCreditPrice(e.target.value)}
                />
                {creditPrice && (
                  <p className="text-xs text-muted-foreground mt-1">
                    = {(parseInt(creditPrice, 10) / 100).toFixed(2)} €
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setCreditForm(false);
                  setCreditSlug("");
                  setCreditPrice("");
                }}
              >
                Annuler
              </Button>
              <Button
                onClick={() => {
                  updateCreditMutation.mutate({
                    categorySlug: creditSlug.trim(),
                    priceCents: parseInt(creditPrice, 10),
                  });
                }}
                disabled={
                  !creditSlug.trim() ||
                  !creditPrice ||
                  isNaN(parseInt(creditPrice, 10)) ||
                  updateCreditMutation.isPending
                }
              >
                <Save className="h-4 w-4 mr-2" />
                {updateCreditMutation.isPending ? "Sauvegarde…" : "Sauvegarder"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Pricing params form dialog */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editKey ? "Modifier" : "Ajouter"} une configuration</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <label htmlFor="pricing-key" className="text-sm font-medium">
                  Clé
                </label>
                <Input
                  id="pricing-key"
                  placeholder="ex: FREE_TENDER_LIMIT"
                  value={formKey}
                  onChange={(e) => setFormKey(e.target.value)}
                  disabled={!!editKey}
                />
              </div>
              <div>
                <label htmlFor="pricing-value" className="text-sm font-medium">
                  Valeur
                </label>
                <Input
                  id="pricing-value"
                  placeholder="ex: 10"
                  value={formValue}
                  onChange={(e) => setFormValue(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="pricing-desc" className="text-sm font-medium">
                  Description (optionnel)
                </label>
                <Input
                  id="pricing-desc"
                  placeholder="ex: Nombre max d'AO pour le plan Free"
                  value={formDesc}
                  onChange={(e) => setFormDesc(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowForm(false)}>
                Annuler
              </Button>
              <Button
                onClick={() =>
                  upsertMutation.mutate({ key: formKey, value: formValue, description: formDesc || undefined })
                }
                disabled={!formKey || !formValue || upsertMutation.isPending}
              >
                <Save className="h-4 w-4 mr-2" />
                Sauvegarder
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
